import { skipToken } from "@reduxjs/toolkit/query";
import { useMemo } from "react";
import { clientApi, useGetClientQuery } from "../Redux/api/clientApi";
import { talentApi, useGetTalentQuery } from "../Redux/api/talentApi";
import { useAppDispatch, useAppSelector } from "../Redux/store";
import { Client, SubscriptionTier } from "../Types/Client";
import { ProfileType, VerificationStatus } from "../Types/General";
import { formatUserName } from "../Utils/String";
import { tier, useSubscription } from "./useSubscription";
import { Talent } from "../Types/Talent";
interface useUserProps {}
export const useUser = () => {
    /* ******************** Hooks ******************** */
    const dispatch = useAppDispatch();
    const { userId, userType } = useAppSelector( state => state.Global)
    const verification_status = useAppSelector( state => state.Websocket.verification_status);
    const type = userType == 'client' ? ProfileType['Client'] : ProfileType['Talent'];
    const talent = useGetTalentQuery( type == 0 && userId || skipToken);
    const client = useGetClientQuery( type == 1 && userId || skipToken);
    const { getSubscription, formatSubscriptionPrice, subscriptionsLoading } = useSubscription();
    /* ******************** Variables ******************** */

    const userSubscription = useMemo(() => {
        const clientSubscription =  client?.data?.subscription;
        const subscription = getSubscription(clientSubscription?.tier ?? 'starter');
        const price = clientSubscription?.type == 'monthly' ? `$${subscription?.monthly_price}/mo` : `$${subscription?.annual_price}/year`
        return {
            title: subscription?.title ?? 'Starter',
            tier: clientSubscription?.tier ?? 'starter' as tier,
            type: clientSubscription?.type ?? 'monthly',
            price: clientSubscription ? price : 'Free',
            createdAt: clientSubscription?.createdAt ?? '',
            updatedAt: clientSubscription?.updatedAt ?? '',
            endDate: clientSubscription?.endDate ?? '',
            renew: clientSubscription?.renew ?? true
        }
    }, [client]);
    const userQuery = useMemo(() => {
        const currUser = type == 0 ? talent : client
        return {
            user: currUser.data as CombinedUser,
            isUserLoading: currUser.isLoading,
            isUserFetching: currUser.isFetching
        }
    }, [talent, client])

    const isActive = userQuery?.user?.active ?? false;
    const isVerified = (verification_status ?? userQuery?.user?.verification_status) === VerificationStatus.verified;
    const locator = userQuery?.user?.locator;
    const hasDefaultPaymentMethod = userQuery?.user?.stripe?.default_payment_id !== null;
    /* ******************** Functions ******************** */
    const getUsername = (full: boolean = false) => {
        if (userQuery?.user?.type === 'company') return userQuery?.user?.company;
        return formatUserName(userQuery?.user?.first_name, userQuery?.user?.last_name, true)
    }
    const invalidateUser = () => {
        if (type === ProfileType.Client) dispatch(clientApi.util.invalidateTags(["ClientData"]));
        if (type === ProfileType.Talent) dispatch(talentApi.util.invalidateTags(["TalentData"]));
    }
    /* ******************** Effects ******************** */
    /* ******************** JSX ******************** */
    return {
        type,
        getUsername,
        userSubscription,
        hasPremium: userSubscription?.tier != SubscriptionTier.Starter,
        isActive,
        locator,
        invalidateUser,
        isVerified,
        hasDefaultPaymentMethod,
        ...userQuery,
    };
}

export type CombinedUser = Talent & Client  |  undefined